import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WhiteMainBackground from '../../../shared_components/WhiteMainBackground/WhiteMainBackground';
import ImageModal from '../ImageModal/ImageModal';
import Spinner from '../../../shared_components/Spinner/Spinner';
import ListHeadingWithSearch from '../../../shared_components/ListHeadingWithSearch/ListHeadingWithSearch';
import PaginationGroup from '../../../shared_components/PaginationGroup/PaginationGroup';
import styles from './ImageGrid.module.css';
import ImageService from '../../../services/ImageService';

const ImageGrid = ({
	handleSearchSubmit,
	handleClearSearch,
	filteredImages,
	setFilteredImages,
	loading,
	currentPage,
	totalPages,
	onPageChange,
	getImageUrl,
	jwtToken,
	userData,
	setCurrentPage,
	searchQuery,
}) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);

	const navigate = useNavigate();

	const handleImageClick = (image) => {
		if (!image) return;

		setSelectedImage(image);
		setModalOpen(true);

		if (!image.base64_medium) {
			ImageService.getImageById(jwtToken, userData, image.imageID)
				.then((fullImage) => {
					const imageWithMedium = { ...image, ...fullImage };
					setSelectedImage(imageWithMedium);
				})
				.catch((error) => {
					alert(
						'Failed to load full image data. Please try again or refresh the page.'
					);
				});
		}
	};
	const closeModal = () => {
		setSelectedImage(null);
		setModalOpen(false);
	};

	const handleDownload = () => {
		ImageService.handleDownload(selectedImage, jwtToken, userData);
	};

	const handleCopyPrompt = () => {
		ImageService.handleCopyPrompt(selectedImage);
	};

	const handleDeleteImage = (imageId) => {
		ImageService.deleteImage(imageId, userData, jwtToken)
			.then(() => {
				// Refresh the current view (search or regular)
				if (searchQuery) {
					return ImageService.searchImages(
						jwtToken,
						userData,
						searchQuery,
						currentPage - 1,
						6
					);
				} else {
					return ImageService.getAllImages(
						jwtToken,
						userData,
						currentPage - 1,
						6
					);
				}
			})
			.then((response) => {
				setFilteredImages(response.content);

				if (response.content.length === 0 && currentPage > 1) {
					setCurrentPage(currentPage - 1);
				}

				closeModal();
			})
			.catch((error) => {
				alert(`Failed to delete the image. Please try again. `);
			});
	};

	const handleViewImage = () => {
		if (selectedImage) {
			navigate(`/gallery/image/${selectedImage.imageID}`);
		}
	};

	return (
		<WhiteMainBackground className={styles.container}>
			<ImageModal
				isOpen={modalOpen}
				onClose={closeModal}
				prompt={selectedImage?.promptUsed}
				onDownload={() => handleDownload(selectedImage)}
				onCopy={handleCopyPrompt}
				onDelete={handleDeleteImage}
				onEdit={handleViewImage}
				assetName={selectedImage?.assetName}
				imageId={selectedImage?.imageID}
				className={styles.modalImage}>
				{selectedImage && (
					<img
						src={`data:image/jpeg;base64,${selectedImage.base64_medium}`}
						alt='Selected'
					/>
				)}
			</ImageModal>
			<div className={styles.searchContainer}>
				<ListHeadingWithSearch searchAction={(searchTerm) => {
					if (searchTerm === '') {
						handleClearSearch();
					} else {
						handleSearchSubmit(searchTerm);
					}
				}}>
					Image Gallery
				</ListHeadingWithSearch>
			</div>
			{loading ? (
				<div className={styles.spinnerContainer}>
					<Spinner />
				</div>
			) : (
				<>
					<div className={styles.gridContainer}>
						{filteredImages.map((image, index) => (
							<div
								key={image.imageID}
								className={styles.gridItem}
								onClick={() => handleImageClick(image)}
								role='button'
								tabIndex={0}
								aria-label={`Image ${index + 1}`}>
								<img
									src={`data:image/jpeg;base64,${image.base64_thumbnail}`}
									alt={`Images ${index + 1}`}
								/>
							</div>
						))}
					</div>
				</>
			)}
			<div className={styles.paginationContainer}>
				<PaginationGroup
					activePage={currentPage}
					numberOfPages={totalPages}
					updatePageClicked={onPageChange}
				/>
			</div>
		</WhiteMainBackground>
	);
};

export default ImageGrid;
